'use client';

import { useRouter } from 'next/navigation';
import { useCallback, useRef, useState } from 'react';

import { useToast } from '@/components/ui/use-toast';
import { config } from '@/config/config';
import useIsMobile from '@/hooks/use-is-mobile.hook';
import { getLogger } from '@/logger/logger';
import { bookActiveCart, type BookingPlatform } from '@/services/cart/book-active-cart.service';
import { clearPhoneConfirmation } from '@/store/auth.store';
import { cartStore, resetCart } from '@/store/cart/cart.store';
import { loadOpenPayments, openPaymentsStore } from '@/store/open-payments/open-payments.store';
import { linkOrderToAnalyticsIdentity } from '@/utils/analytics/identity.util';
import { trackPurchaseFinished } from '@/utils/cart/cart-analytics.util';
import { OPEN_PAYMENTS_ROUTE } from '@/utils/consts/open-payments.consts';
import { visualPhonePatientSac } from '@/utils/consts/sac.consts';
import { buildUrlWithPersistedParams } from '@/utils/persisted-params.util';

export const CHECKOUT_PATH = 'payments';

export const CHECKOUT_ORDER_PARAM = 'orderId';

export const BOOKING_ERROR_MESSAGE = {
  slotUnavailable:
    'Um dos horários escolhidos não está mais disponível. Escolha outro horário para continuar.',
  cartNotFound: 'Não encontramos seu carrinho. Monte o carrinho de novo para continuar.',
  unexpected: 'Não foi possível abrir o pagamento. Tente novamente.',
  unconfirmed: 'Não conseguimos confirmar seu pedido. Tente novamente.',
  alreadyCreated: 'Seu pedido pode já ter sido criado. Confira em Pagamentos pendentes.',
  customerBlocked: 'Sua conta não pode fazer novas compras no momento. '
    + `Para entender o motivo, fale com a gente pelo WhatsApp ${visualPhonePatientSac}.`,
};

const UNCONFIRMED_BOOKING_STATUSES = [502, 503];

const logger = getLogger('cart.checkout-navigation');

export function buildCheckoutLink(orderId: string): string {
  const base = config.checkout.url.replace(/\/$/, '');
  const query = new URLSearchParams({ [CHECKOUT_ORDER_PARAM]: orderId });

  return buildUrlWithPersistedParams({
    baseUrl: `${base}/${CHECKOUT_PATH}?${query.toString()}`,
  });
}

function readStatus(cause: unknown): number | undefined {
  return (cause as { status?: number })?.status;
}

function isCustomerBlocked(cause: unknown): boolean {
  const name = (cause as { data?: { name?: string } })?.data?.name;

  return readStatus(cause) === 403 && name === 'CustomerBlockedException';
}

function isUnconfirmedBooking(cause: unknown): boolean {
  const status = readStatus(cause);

  return cause instanceof TypeError || (status !== undefined && UNCONFIRMED_BOOKING_STATUSES.includes(status));
}

function bookingErrorMessage(cause: unknown): string {
  const status = readStatus(cause);

  if (isCustomerBlocked(cause)) {
    return BOOKING_ERROR_MESSAGE.customerBlocked;
  }

  if (status === 409) {
    return BOOKING_ERROR_MESSAGE.slotUnavailable;
  }

  if (status === 404) {
    return BOOKING_ERROR_MESSAGE.cartNotFound;
  }

  return BOOKING_ERROR_MESSAGE.unexpected;
}

export function useCheckoutNavigation() {
  const [isOpening, setIsOpening] = useState(false);
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const router = useRouter();
  const lastBookingUnconfirmed = useRef(false);

  const openCheckout = useCallback(async (): Promise<void> => {
    const platform: BookingPlatform = isMobile ? 'web mobile' : 'web';

    setIsOpening(true);

    try {
      const { invoiceId } = await bookActiveCart(platform);

      trackPurchaseFinished(cartStore.getState().cart?.items ?? [], invoiceId);
      linkOrderToAnalyticsIdentity(invoiceId);
      window.location.assign(buildCheckoutLink(invoiceId));
    } catch (cause) {
      if (lastBookingUnconfirmed.current && readStatus(cause) === 404) {
        lastBookingUnconfirmed.current = false;

        const outcome = await loadOpenPayments();
        const hasOpenPayment = outcome !== 'loaded' || openPaymentsStore.getState().payments.length > 0;

        logger.info('Cart gone after an unconfirmed booking', {
          cart: { event: 'cart_gone_after_unconfirmed', open_payments_load: outcome, has_open_payment: hasOpenPayment },
        });

        if (hasOpenPayment) {
          resetCart();
          clearPhoneConfirmation();
          toast.info(BOOKING_ERROR_MESSAGE.alreadyCreated);
          router.push(OPEN_PAYMENTS_ROUTE);
          setIsOpening(false);
          return;
        }
      }

      if (isUnconfirmedBooking(cause)) {
        lastBookingUnconfirmed.current = true;
        logger.error('Failed to create the order before opening the checkout', {
          cart: { event: 'order_creation_unconfirmed' },
          error: cause,
        });
        toast.error(BOOKING_ERROR_MESSAGE.unconfirmed);
        setIsOpening(false);
        return;
      }

      lastBookingUnconfirmed.current = false;
      const message = bookingErrorMessage(cause);

      if (isCustomerBlocked(cause)) {
        logger.info('Order refused for a blocked customer', { cart: { event: 'order_creation_customer_blocked' } });
      } else {
        logger.error('Failed to create the order before opening the checkout', {
          cart: { event: 'order_creation_failed' },
          error: cause,
        });
      }
      resetCart();
      clearPhoneConfirmation();
      toast.error(message);
      router.push('/');
      setIsOpening(false);
    }
  }, [isMobile, router, toast]);

  return { openCheckout, isOpening };
}
